/**
 * TAS-622 — Insert/Delete change-log rows carry a JSON "image" of the whole row
 * (API: timesheetChangeLogUtil.entityImageJson — DB column name → rendered
 * string | null; datetimes ISO UTC; offsets like '-07:00'). Pure helpers for the
 * HistoryPanel: parse tolerantly, then split into labelled key fields + the rest.
 */

export type ChangeLogImage = Record<string, string | null>;

/**
 * Mirrors the API's parseEntityImage: null for null/empty, unparseable JSON, or
 * anything that is not a plain JSON object. Never throws — a bad row degrades to
 * the plain "Added"/"Deleted" line instead of breaking the panel.
 */
export function parseChangeLogImage(json: string | null | undefined): ChangeLogImage | null {
  if (json == null || json === '') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const image: ChangeLogImage = {};
  Object.entries(parsed as Record<string, unknown>).forEach(([k, v]) => {
    if (v === null || v === undefined) image[k] = null;
    else if (typeof v === 'string') image[k] = v;
    else if (typeof v === 'number' || typeof v === 'boolean') image[k] = String(v);
    else image[k] = JSON.stringify(v);
  });
  return image;
}

/** Key fields, in display order: label + the column names that can carry it
 *  (first present wins). Anything not listed lands in `rest`. */
const KEY_FIELDS: ReadonlyArray<{ label: string; keys: readonly string[] }> = [
  { label: 'Date', keys: ['EntryDate', 'WorkDate', 'ClaimDate'] },
  { label: 'In', keys: ['InDateTime', 'StartDateTime'] },
  { label: 'Out', keys: ['OutDateTime', 'EndDateTime'] },
  { label: 'Type', keys: ['Type', 'EntryType', 'TimeEntryType'] },
  { label: 'Punch in', keys: ['PunchTypeIn'] },
  { label: 'Punch out', keys: ['PunchTypeOut'] },
  { label: 'Project', keys: ['ProjectID'] },
  { label: 'Account', keys: ['AccountCodeID', 'AccountID'] },
  { label: 'Phase', keys: ['ProjectPhaseID'] },
  { label: 'SO', keys: ['ServiceOrderText', 'ServiceOrderID'] },
];

export interface ImageField {
  label: string;
  /** The DB column name — what value formatting keys off. */
  field: string;
  value: string | null;
}

export interface SplitImage {
  key: ImageField[];
  /** Remaining non-null columns, in image order. */
  rest: ImageField[];
}

export function splitChangeLogImage(image: ChangeLogImage): SplitImage {
  const used = new Set<string>();
  const key: ImageField[] = [];
  KEY_FIELDS.forEach(({ label, keys }) => {
    const hit = keys.find((k) => Object.prototype.hasOwnProperty.call(image, k));
    if (hit == null) return;
    keys.forEach((k) => {
      if (Object.prototype.hasOwnProperty.call(image, k)) used.add(k);
    });
    // SO: prefer the text, but fall back to the ID when the text is empty.
    const present = keys.filter((k) => Object.prototype.hasOwnProperty.call(image, k));
    const withValue = present.find((k) => image[k] != null && image[k] !== '') ?? hit;
    key.push({ label, field: withValue, value: image[withValue] });
  });
  const rest: ImageField[] = [];
  Object.keys(image).forEach((k) => {
    if (used.has(k)) return;
    const v = image[k];
    if (v == null || v === '') return;
    rest.push({ label: k, field: k, value: v });
  });
  return { key, rest };
}
